
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { fixLlmBotPermissions } from '@/utils/llmBotPermissions';
import Header from '@/components/Header';
import { AdminNav } from '@/components/admin/AdminNav';

const Settings = () => {
  const { isAdmin } = useAuth();
  const [timeoutMinutes, setTimeoutMinutes] = useState<number>(20);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fixingPermissions, setFixingPermissions] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!isAdmin) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'admin_session_timeout_minutes')
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw error;
        }

        if (data?.value) {
          setTimeoutMinutes(parseInt(data.value));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [isAdmin]);

  const handleSave = async () => {
    if (!isAdmin) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: 'admin_session_timeout_minutes',
          value: timeoutMinutes.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFixLlmBotPermissions = async () => {
    if (!isAdmin) return;

    setFixingPermissions(true);
    try {
      const result = await fixLlmBotPermissions();
      
      if (result.success) {
        toast.success('LLM Bot permissions fixed successfully');
      } else {
        toast.error(result.message || 'Failed to fix LLM Bot permissions');
      }
    } catch (error) {
      console.error('Error fixing LLM Bot permissions:', error);
      toast.error('Failed to fix LLM Bot permissions');
    } finally {
      setFixingPermissions(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <AdminNav />
      </div>
      <div className="max-w-2xl mx-auto space-y-6">
        
        <Card>
          <CardHeader>
            <CardTitle>Security Settings</CardTitle>
            <CardDescription>
              Configure security-related settings for the application
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="timeoutMinutes">
                Admin Session Timeout (minutes)
              </Label>
              <Input
                id="timeoutMinutes"
                type="number"
                min="1"
                max="480"
                value={timeoutMinutes}
                onChange={(e) => setTimeoutMinutes(parseInt(e.target.value) || 1)}
                placeholder="20"
                disabled={loading || saving}
              />
              <p className="text-sm text-gray-500">
                Admin users will be automatically logged out after this period of inactivity. 
                Range: 1-480 minutes (8 hours max).
              </p>
            </div>
            
            <Button 
              onClick={handleSave} 
              disabled={loading || saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* <Card>
          <CardHeader>
            <CardTitle>Database Management</CardTitle>
            <CardDescription>
              One-time database operations and maintenance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>LLM Bot Permissions Fix</Label>
              <p className="text-sm text-gray-500">
                Run this once to fix llm_bot permissions on existing Fivetran databases. 
                This grants proper schema and table access to existing databases that were 
                created before the permission system was in place.
              </p>
              <Button 
                onClick={handleFixLlmBotPermissions} 
                disabled={fixingPermissions}
                variant="outline"
                className="w-full"
              >
                {fixingPermissions ? 'Fixing Permissions...' : 'Fix LLM Bot Permissions'}
              </Button>
            </div>
          </CardContent>
        </Card> */}
      </div>
      </div>
    </div>
  );
};

export default Settings;
